import { getExperiencePurchaseItems, isGift, isPackagePurchase, PurchaseActionOption } from "common";
import React, { useState } from "react";
import { ExperiencePurchaseItem, Purchase, PurchaseItem } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { isBookingEngine, isCodeSupported } from "../../../models/PurchaseItem";
import { PaymentBreakdown } from "../../PurchaseModification/PaymentBreakdown";
import { ApplyCouponModal } from "../ApplyCouponModal";
import { CollectBalanceButton } from "./CollectBalance";
import { ReturnExcessPaymentButton } from "./ReturnExcessPaymentButton";
import { usePostCheckinWarningModal } from "@/hooks/usePostCheckinWarningModal";

interface PaymentSummaryProps {
    purchase: Purchase;
    item?: PurchaseItem;
}

export const PaymentSummary = ({ purchase, item }: PaymentSummaryProps) => {
    const seller = useSeller();
    const [currentAction, setCurrentAction] = useState<PurchaseActionOption | null>(null);
    const { PostCheckInWarningModal, withPostCheckInWarning } = usePostCheckinWarningModal(item);

    const singleItemPurchase = purchase.items.length === 1;
    const canApplyCode = isPackagePurchase(purchase) || purchase.items.some((item) => isCodeSupported(item.object));
    const purchaseItem = item ?? getExperiencePurchaseItems(purchase)[0] ?? purchase.items[0]; // Introduced this var instead of reassigning to item, since we need item-undefined logic for Mist element
    const isGiftItem = singleItemPurchase && isGift(purchaseItem);

    const isBookingEnginePurchaseEditable =
        seller.preferences.booking.allowBookingEngineOrderModification || !isBookingEngine(purchaseItem.source);

    return (
        <div className="h-max rounded-lg bg-gray-lighter p-6">
            <PaymentBreakdown
                isEditable={!isGiftItem && isBookingEnginePurchaseEditable}
                purchase={purchase}
                item={item}
                onApplyCode={withPostCheckInWarning(() => setCurrentAction(PurchaseActionOption.APPLY_CODE))}
            />

            <div className="mt-6 mb-3 text-right">
                <CollectBalanceButton purchase={purchase} item={item} />
            </div>
            <ReturnExcessPaymentButton purchase={purchase} item={item} />

            {purchaseItem && canApplyCode && currentAction === PurchaseActionOption.APPLY_CODE && (
                <ApplyCouponModal
                    isOpen={currentAction === PurchaseActionOption.APPLY_CODE}
                    purchase={purchase}
                    item={purchaseItem as ExperiencePurchaseItem}
                    onClose={() => setCurrentAction(null)}
                />
            )}

            <PostCheckInWarningModal />
        </div>
    );
};
